import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { z } from "zod";
import { ApiError } from "@/api/SolarCloudApi";
import { FormCard } from "@/components/FormCard";
import { autoSignIn, confirmSignUp } from "aws-amplify/auth";
import { FormState, useAuthStore } from "@/stores/authStore";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { verificationCodeSchema } from "@/model/authSchema";
import { useAuth } from "@/hooks/useAuth";
import { usePixelContext } from "@/contexts/MetaPixelContext";
import { TrackableEventNameEnum } from "react-meta-pixel";

type TVerificationCodeSchema = z.infer<typeof verificationCodeSchema>;

type VerificationFormProps = {
  handleVerified: () => void;
  email?: string;
};

export default function VerificationForm({
  handleVerified,
  email,
}: VerificationFormProps) {
  const { pixel } = usePixelContext()
  const { setFormState, data } = useAuthStore();
  const { handleSignIn, handleSendVerification } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<TVerificationCodeSchema>({
    resolver: zodResolver(verificationCodeSchema),
    defaultValues: {
      email: email || data.email || "",
      code: "",
    },
  });

  const resend = async () => {
    form.clearErrors("code");
    form.setValue("code", "");
    setIsResending(true);
    await handleSendVerification(email || data.email!, true);
    setIsResending(false);
  };

  const trackVerification = () => {
          if(pixel){
                pixel.trackEvent(TrackableEventNameEnum.CompleteRegistration)
              }
  }

  const onSubmit = async (formData: TVerificationCodeSchema) => {
    try {
      const { nextStep: confirmSignUpNextStep } = await confirmSignUp({
        username: formData.email!,
        confirmationCode: formData.code!,
      });

      if (confirmSignUpNextStep.signUpStep === "DONE") {
        if (!data.password) {
          trackVerification()
          handleVerified();
          return;
        }
        await handleSignIn(data.email!, data.password!, form, handleVerified);
      }

      if (confirmSignUpNextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        const { nextStep } = await autoSignIn();

        if (nextStep.signInStep === "DONE") {
          await queryClient.invalidateQueries({ queryKey: ["authentication"] });
          trackVerification()
          handleVerified();
        }
      }
    } catch (error: ApiError | any) {
      console.error("Verification error:", error);
      if (error.name === "CodeMismatchException") {
        form.setError("code", {
          type: "manual",
          message: error.message || "Invalid verification code",
        });
      }

      if (error instanceof ApiError) {
        const code = error?.body?.code;
        // if (code === "ALREADY_VERIFIED") {
        //   await signIn({ username: data.email!, password: data.password! });
        //   await queryClient.invalidateQueries({ queryKey: ["authentication"] });
        //   setFormState(FormState.SIGNIN);
        // } else
        if (code === "CodeMismatchException") {
          form.setError("code", {
            type: "manual",
            message: "Invalid verification code",
          });
        } else if (code === "ExpiredCodeException") {
          form.setError("code", {
            type: "manual",
            message: "Verification code has expired",
          });
        } else {
          form.setError("code", {
            type: "manual",
            message: "Something went wrong",
          });
        }
      }
    }
  };

  return (
    <>
      {!email && (
        <button
          onClick={() => setFormState(FormState.SIGNUP)}
          className="w-full max-w-lg mx-auto px-6 flex gap-2 text-muted-foreground cursor-pointer"
        >
          <ArrowLeft />
          Back to Sign Up
        </button>
      )}
      <FormCard
        form={form}
        onSubmit={onSubmit}
        title="Verify your email"
        description="Enter the verification code sent to your email"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="you@example.com"
                  {...field}
                  disabled={!!email || !!data.email}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Verification Code</FormLabel>
              <FormControl>
                <Input placeholder="123456" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={form.formState.isSubmitting}
        >
          Verify
        </Button>
      </FormCard>
      <div className="mb-4 text-center text-sm">
        Didn't receive your code?{" "}
        <button
          disabled={isResending}
          onClick={resend}
          className="underline underline-offset-4 cursor-pointer"
        >
          Resend
        </button>
      </div>
    </>
  );
}
